import axios from "axios";
import { getAssetToken, clearAssetToken } from "@/lib/assetToken";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

// ─── WHICH SIGN-IN THIS TAB IS ACTING AS (ZEN-432, ZEN-433) ──────────────────
//
// THE PROBLEM IN ONE SENTENCE: the sign-in pass lives in browser storage that
// EVERY tab of this site shares, but each tab keeps its own idea of who is
// signed in — so the two drift apart and nothing notices.
//
// Concretely. A person has the app open in two tabs. In tab two they sign in as
// somebody else (the demo family, a second parent's account, their own account
// again after a password reset). Tab one still shows the first person's name,
// their beneficiaries and their figures — but the line below reads the SHARED
// storage on every single request, so every request tab one now sends goes out
// as the NEW account. A form already open in tab one submits the first family's
// values into the second family's records, and nobody is told.
//
// So a request has to belong to a sign-in, not merely to a tab:
//
//   * PINNING. Whoever establishes identity (AuthContext) pins the exact pass
//     it authenticated with. If the stored pass later differs from the pin, this
//     tab is out of date and its requests are refused HERE, before anything is
//     sent. That closes the write hole immediately — it does not wait for a
//     screen to re-render, and it holds even for code already mid-flight.
//
//   * STAMPING. Every request records the pass it was sent under, so the
//     failure handler below can tell a genuine "your session is over" from the
//     late reply of a session that has already been replaced. Without it, an old
//     request finishing after a new sign-in deletes the NEW pass — which is
//     defect (1) of ZEN-432.
//
// No pin means no opinion: the sign-in and sign-up screens have nothing to
// compare against yet, and must keep working.
let pinnedToken = null;

/** Record the sign-in pass this tab is operating as. */
export function pinSessionToken(token) {
  pinnedToken = token || null;
}

/** Forget it (signing out), so the sign-in screen can talk to the server again. */
export function clearSessionPin() {
  pinnedToken = null;
}

/** What this tab believes it is signed in with, or null before it knows. */
export function pinnedSessionToken() {
  return pinnedToken;
}

// DRAFT COPY — NOT YET APPROVED (rule 13). Shown if a person happens to be
// mid-action at the instant another tab replaces the sign-in. Rare, because the
// screen in AuthContext takes over within the same tick, but it is real text a
// person can see, so it is written as a sentence rather than a code word.
export const STALE_SESSION_MESSAGE =
  "Your sign-in changed in another tab, so this was not sent.";

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (pinnedToken && token !== pinnedToken) {
    const err = new Error(STALE_SESSION_MESSAGE);
    // Marks the refusal as OURS. A caller that needs to tell this apart from a
    // server answer checks this flag; nothing does today, so no predicate is
    // exported for it.
    err.isStaleSession = true;
    err.config = config;
    return Promise.reject(err);
  }
  // The pass this request is going out under, kept for the handler below.
  config.__sessionToken = token || null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── AFTER A SAVE, EVERY FIGURE ON SCREEN RE-CHECKS (ZEN-735) ───────────────
//
// THE PROBLEM IN ONE SENTENCE: each screen decided for itself which figures to
// refresh after it saved something, so a save made through a screen that
// forgot the dashboard left the dashboard showing money from before the save.
//
// On 6 October a settlement card read $50.77 for a day while the Awaiting list
// under it, fetched fresh, added up to $71.93: the CAD 30.00 expense entered in
// between had never told the card. 93 hand-written refresh calls in 25 screens
// were each one screen's guess at what a save can change.
//
// So the answer lives HERE, at the one door every save goes through: a save
// that succeeds is announced, and lib/queryClient.js answers by re-checking
// every figure on screen. A new screen cannot forget, because it does nothing.
//
// "A save" is any request that is not a read. A request that changes nothing
// but uses a save-style method (signing in, say) costs one harmless re-check.
// A READ made with a save-style method would loop — the re-check would issue
// it again — and lib/queryClient.test.js fails if one ever appears.
const READS = new Set(["get", "head", "options"]);
const afterSave = new Set();

/** Be told whenever a save succeeds. Returns the function that stops it. */
export function onSaveSucceeded(listener) {
  afterSave.add(listener);
  return () => afterSave.delete(listener);
}

api.interceptors.response.use(
  (r) => {
    if (!READS.has((r.config?.method || "get").toLowerCase())) {
      for (const listener of afterSave) {
        // The save HAS happened. A refresh that fails must not turn it into
        // an error the person sees and then repeats.
        try { listener(r.config); } catch (e) { console.error("refresh after save failed", e); }
      }
    }
    return r;
  },
  async (err) => {
    await readRefusedFile(err);
    // ONLY act on a refusal that belongs to the sign-in currently in storage.
    //
    // A request sent under a pass that has since been replaced is answering a
    // question nobody is asking any more. Treating its 401 as "you are signed
    // out" throws away the pass of whoever is signed in NOW — the person is
    // dumped at the sign-in screen one moment after successfully signing in.
    // That is ZEN-432 defect (1), and this comparison is what ends it.
    const sentUnder = err.config?.__sessionToken ?? null;
    const storedNow = localStorage.getItem("token");
    if (
      err.response?.status === 401 &&
      sentUnder === storedNow &&
      !window.location.pathname.startsWith("/auth")
    ) {
      // The session is gone; its asset token must go with it. Without this the
      // credential outlives the session that authorised it and is still in
      // memory when the next person logs in.
      clearAssetToken();
      localStorage.removeItem("token");
      clearSessionPin();
      window.location.href = "/auth";
    }
    return Promise.reject(err);
  }
);

// The server's own sentence about a refusal, or null when it sent none.
export function refusalSentence(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  // A structured 422 carries both a human message and the machine-readable field
  // names. Without this branch String(d) renders the literal "[object Object]" to
  // the person who has to fix the form — which is what the student submission
  // screen has been showing for a missing field. (ZEN-91)
  if (d && typeof d === "object" && typeof d.detail === "string") return d.detail;
  return String(d);
}

export function apiErr(e) {
  return refusalSentence(e) ?? (e.message || "Something went wrong.");
}

// A REFUSED FILE STILL CARRIES ITS REASON. (ZEN-327)
//
// A request for a file gets its refusal back wrapped as a file too, so the
// sentence the server wrote sat where apiErr could not see it and the family
// read "Request failed with status code 409". This unwraps it, once, for every
// request that asked for a file, so apiErr reads it like any other refusal.
export const FILE_REFUSED_WITHOUT_A_REASON =
  "This was not created, and no reason was given. Nothing in your records was changed. Please try again in a moment, and contact support if it keeps happening.";

async function readRefusedFile(err) {
  if (err?.config?.responseType !== "blob" || !err.response) return;
  const body = err.response.data;
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    let said = null;
    try { said = JSON.parse(await body.text()); } catch { /* not a sentence */ }
    err.response.data = said;
  }
  if (refusalSentence(err) == null) err.message = FILE_REFUSED_WITHOUT_A_REASON;
}

// ─── File URLs ───────────────────────────────────────────────────────────────
//
// These carry a SHORT-LIVED ASSET TOKEN, never the session token. The URLs end
// up in CDN access logs, browser history and forwarded links; a session token
// there would be a seven-day master key sitting in all three. An asset token
// expires in minutes and the API refuses it on anything but file routes.
//
// Builders stay synchronous because they are called inline in JSX
// (`src={receiptUrl(id)}`); the token is fetched once after login and kept
// fresh on a timer by lib/assetToken.js.

function assetParam() {
  const t = getAssetToken();
  return t ? `token=${encodeURIComponent(t)}` : "";
}

// receipt file URL for <img> tags
export function receiptUrl(id) {
  return `${API}/receipts/${id}/file?${assetParam()}`;
}

// receipt preview URL — PDFs auto-converted to PNG for clean viewing
export function receiptPreviewUrl(id, page = 0) {
  return `${API}/receipts/${id}/preview?${assetParam()}&page=${page}`;
}

// A PICTURE OF ONE PAGE, for every stored file the server can draw page by
// page (ZEN-419): receipts at their own address, and expense, withdrawal and
// school paperwork documents at the file's address with "/preview" added.
// null when the server has no picture for this attachment.
const PAGE_PICTURE_DOCUMENT =
  /\/api((?:\/expenses|\/withdrawals)\/[^/?]+\/documents\/[^/?]+|\/students\/[^/?]+\/coa-docs\/[^/?]+|\/students\/coa-uploads\/[^/?]+)(?:\?|$)/;

export function pagePictureUrl(attachment, page = 0) {
  if (!attachment?.fileUrl) return null;
  if (attachment.receiptId) return receiptPreviewUrl(attachment.receiptId, page);
  const m = PAGE_PICTURE_DOCUMENT.exec(attachment.fileUrl);
  return m ? `${API}${m[1]}/preview?${assetParam()}&page=${page}` : null;
}

// 529 withdrawal supporting-document URL
export function withdrawalDocUrl(id) {
  return `${API}/withdrawals/${id}/document?${assetParam()}`;
}

// 529 withdrawal individual document URL by doc_id
export function withdrawalDocUrlById(wid, docId) {
  return `${API}/withdrawals/${wid}/documents/${docId}?${assetParam()}`;
}

// ONE builder for every attachment link. (ZEN-79)
//
// The server returns an API PATH, never a storage key and never a finished
// URL: the asset token is appended here, in the browser, and the click-refresh
// hook only rewrites links that already carry one. A URL handed over ready-made
// by the server would 401 the moment it was clicked.
export function assetUrl(path) {
  return `${API}${path}?${assetParam()}`;
}

// EVERY DOWNLOAD OF RECORDS GOES THROUGH HERE. (ZEN-327)
//
// Not a URL builder on purpose. These return RECORDS, not files, so no
// credential for them may sit in an address that gets logged. This fetches with
// the session in the Authorization header and hands the browser a blob, so the
// download still "just works" from a click with nothing in the URL at all.
//
// `filename` is the name to save under, or a function of the reply that
// returns it. A refusal rejects with the server's sentence readable by apiErr.
export async function downloadFile(path, filename, params) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = typeof filename === "function" ? filename(res) : filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick: Safari needs the object URL to survive the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return res;
}

// CSV / PDF export of the expense records.
export function downloadExport(kind, params = {}) {
  const stamp = params.tax_year ? `-${params.tax_year}` : "";
  return downloadFile(`/export/${kind}`, `zen529-export${stamp}.${kind}`, params);
}

// COA period document URLs — served by id, never by storage path. The server
// resolves the path from the tenant-scoped record and authorizes the
// beneficiary, so a URL can only ever reach the one document it names.
export function coaDocUrl(studentId, docId) {
  return `${API}/students/${studentId}/coa-docs/${docId}?${assetParam()}`;
}

// A just-uploaded document not yet saved onto the student (upload registry).
export function coaUploadUrl(uploadId) {
  return `${API}/students/coa-uploads/${uploadId}?${assetParam()}`;
}

// Legacy path-based form; only reachable for docs with no id in local state.
// The server hardened this route to record-resolution; deleted in release N+1.
export function coaDocLegacyUrl(path) {
  return `${API}/students/coa-doc-serve?p=${encodeURIComponent(path)}&${assetParam()}`;
}

export default api;
